import GameSignals from '@/signals/GameSignals'
import { Model } from '@/model/Model'
import { toMajorUnits } from '@/utils/CurrencyUnits'

export const resolvePromoWinAmount = (promoDetails?: any, fallback?: number): number => {
    const currency = Number(promoDetails?.win_amount_currency)
    const rawWin = Number(promoDetails?.win_amount)
    if (Number.isFinite(currency) && currency >= 0 && (!Number.isFinite(rawWin) || currency !== rawWin)) {
        return currency
    }

    const minor = Number.isFinite(rawWin) ? rawWin : Number(fallback)
    if (!Number.isFinite(minor) || minor < 0) {
        return 0
    }

    return toMajorUnits(minor)
}

/**
 * Isolated PFS lifecycle. Does not change approved FS / H&W / balance paths
 * except: after Accept, skip stake debit on promo spins. Balance comes from
 * player.balance on each spin and is shown when the win is presented.
 */
export class PromoSessionManager {
    private readonly _model: Model
    private _stakeBeforePromo: number | null = null
    private _finalStateMeterDelayId: number | null = null

    public constructor(model: Model) {
        this._model = model
        ;(GameSignals as any).promoFreeSpinsAccepted?.connect(this.onAccepted)
        ;(GameSignals as any).promoFreeSpinsSessionComplete?.connect(this.onSessionComplete)
        ;(GameSignals as any).promoFreeSpinsDeclined?.connect(this.onDeclined)
        GameSignals.showRegularWin.connect(this.onShowRegularWin)
        GameSignals.finalState.connect(this.onFinalState)
        GameSignals.bonusStopComplete.connect(this.syncWinMeter)
    }

    private onAccepted = (data?: any) => {
        const promoDetails = data?.promo_details ?? data?.next_round ?? data
        if (!promoDetails || typeof promoDetails !== 'object') {
            return
        }

        const current = (this._model.spinData || {}) as any
        const promoType = String(promoDetails?.type || data?.next_round?.type || 'promospins').toLowerCase()
        const resolvedType = promoType === 'promospin' || promoType === 'promospins' ? promoType : 'promospins'
        const numSpins = Number(promoDetails?.num_spins ?? current?.promo_details?.num_spins)
        const spinsLeft = Number(promoDetails?.spins_left ?? current?.promo_details?.spins_left ?? numSpins)
        const resolvedNumSpins = Number.isFinite(numSpins) && numSpins > 0 ? numSpins : 0
        const resolvedSpinsLeft = Number.isFinite(spinsLeft) && spinsLeft >= 0 ? spinsLeft : resolvedNumSpins
        const totalWin = resolvePromoWinAmount(promoDetails, current?.freebet?.total_win)
        const promoStake = this.resolvePromoStake(promoDetails)

        this._model.spinData = {
            ...current,
            promo_details: {
                ...(current.promo_details || {}),
                ...promoDetails,
                type: resolvedType,
                num_spins: resolvedNumSpins || promoDetails?.num_spins,
                spins_left: resolvedSpinsLeft,
            },
            amount_type: String(promoDetails?.amount_type ?? current.amount_type ?? '3'),
            freebet: {
                ...(current.freebet || {}),
                amount_left: resolvedSpinsLeft,
                total_amount_given: resolvedNumSpins || current?.freebet?.total_amount_given || resolvedSpinsLeft,
                total_win: Number.isFinite(totalWin) ? totalWin : 0,
                total_coin_win: Number.isFinite(totalWin) ? totalWin : current?.freebet?.total_coin_win || 0,
                coin_amount: promoStake.numCoins,
                coin_value: promoStake.coinValue,
                logic: current?.freebet?.logic ?? { level: 0, lines_played: 0 },
                triggered_win_amount: current?.freebet?.triggered_win_amount ?? 0,
                triggered_win_amount_currency: current?.freebet?.triggered_win_amount_currency ?? 0,
                triggered_win_bonus_amount: current?.freebet?.triggered_win_bonus_amount ?? 0,
                triggered_win_bonus_amount_currency: current?.freebet?.triggered_win_bonus_amount_currency ?? 0,
            },
        }
        this._model.beginPromoSession()
        this.applyPromoStake(promoStake)
        this.syncWinMeter()
        this.startPromoSpin()
    }

    private onShowRegularWin = () => {
        this.clearFinalStateMeterDelay()
        this.syncWinMeter()
    }

    private onFinalState = () => {
        if (!this.shouldDrivePromoWinMeter()) {
            return
        }

        this.clearFinalStateMeterDelay()
        // Same beat as RegularWinnings (0.3s after Final). Covers PFS wins with no payline combo.
        this._finalStateMeterDelayId = window.setTimeout(() => {
            this._finalStateMeterDelayId = null
            this.syncWinMeter()
        }, 300)
    }

    private clearFinalStateMeterDelay(): void {
        if (this._finalStateMeterDelayId == null) {
            return
        }

        window.clearTimeout(this._finalStateMeterDelayId)
        this._finalStateMeterDelayId = null
    }

    private shouldDrivePromoWinMeter(): boolean {
        return (
            this._model.isPromoSpinsRound() &&
            !this._model.roundState?.isFreeSpins &&
            !this._model.spinState?.isHoldAndWinActive
        )
    }

    private resolvePromoStake(promoDetails?: any): { coinValue: number; numCoins: number; stake: number } {
        const rawCoinValue = Number(promoDetails?.coin_value)
        const rawNumCoins = Number(promoDetails?.num_coins)
        const coinValue =
            Number.isFinite(rawCoinValue) && rawCoinValue > 0 ? rawCoinValue : this._model.currentCoinBet
        const numCoins =
            Number.isFinite(rawNumCoins) && rawNumCoins > 0 ? rawNumCoins : this._model.fixedCoinsBet
        const stake = Number((coinValue * numCoins).toFixed(2))

        return { coinValue, numCoins, stake }
    }

    private applyPromoStake(promoStake: { stake: number }): void {
        if (!(promoStake.stake > 0)) {
            return
        }

        if (this._stakeBeforePromo == null) {
            this._stakeBeforePromo = this._model.currentBet
        }

        this._model.currentBet = promoStake.stake
    }

    private restoreStake(): void {
        if (this._stakeBeforePromo == null) {
            return
        }

        this._model.currentBet = this._stakeBeforePromo
        this._stakeBeforePromo = null
    }

    private syncWinMeter = () => {
        if (!this.shouldDrivePromoWinMeter()) {
            return
        }

        const winAmount = resolvePromoWinAmount(this._model.spinData?.promo_details)
        if (!(winAmount > 0)) {
            return
        }

        ;(GameSignals as any).setWinPanelValue?.emit(winAmount)
    }

    private onSessionComplete = (data?: any) => {
        const nextBalance = Number(
            data?.player?.balance ?? data?.balance ?? data?.promo_details?.balance ?? this._model.spinData?.balance
        )
        this.restoreStake()
        this._model.endPromoSession(Number.isFinite(nextBalance) ? nextBalance : undefined)
        GameSignals.balanceHasChanged.emit(this._model.balance)
    }

    private onDeclined = () => {
        this.restoreStake()
        this._model.endPromoSession()
        this.clearPromoOffer()
    }

    /**
     * Drops the offer the player just turned down. Ending the session alone left its spin count in
     * spinData, and the spin parser carries that block forward for as long as it believes a promo
     * is running — so a later resume, on an offer the server had already cancelled, read a count
     * that could never reach zero.
     */
    private clearPromoOffer(): void {
        const current = this._model.spinData as any
        if (!current?.promo_details) {
            return
        }

        this._model.spinData = { ...current, promo_details: null }
    }

    private startPromoSpin = () => {
        window.setTimeout(() => {
            GameSignals.spinButtonClicked.emit()
        }, 100)
    }
}
